
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Activity, Leaf, Wind, Utensils, CheckCircle2, BookOpen, ShieldCheck, Brain, HeartPulse, Sparkles, Target, PersonStanding, Star, BookMarked, X, Flower2 } from 'lucide-react';

interface PlatformPageProps {
    title: string;
    subtitle: string;
    heroImage?: string;
    children: React.ReactNode;
    moduleName: string;
    icon: React.ReactNode;
    ctaTitle?: string;
    ctaDescription?: string;
}

const PlatformPageLayout: React.FC<PlatformPageProps> = ({ title, subtitle, heroImage, children, moduleName, icon, ctaTitle, ctaDescription }) => {
    const bgImage = heroImage || 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?auto=format&fit=crop&q=80';
    return (
        <div className="bg-sage-50 min-h-screen pb-16">
            {/* Breadcrumbs */}
            <div className="bg-white border-b border-sage-100 py-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/" className="inline-flex items-center text-sage-600 hover:text-sage-800 text-sm font-medium transition-colors">
                        <ArrowLeft size={16} className="mr-2" />
                        Back to Home
                    </Link>
                </div>
            </div>

            {/* Hero Section */}
            <div className="relative bg-sage-900 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 bg-cover bg-center mix-blend-overlay"
                    style={{ backgroundImage: `url('${bgImage}')` }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-b from-sage-900/50 to-sage-900/90"></div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 flex flex-col items-center text-center">
                    <div className="p-4 bg-sage-800/50 rounded-full mb-6 backdrop-blur-sm border border-sage-500/30 shadow-xl">
                        {icon}
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight drop-shadow-sm">
                        {title}
                    </h1>
                    <p className="text-xl md:text-2xl text-sage-100 max-w-3xl font-light drop-shadow-sm">
                        {subtitle}
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
                <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-sage-100">
                    {children}
                </div>
            </div>

            {/* CTA Section */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 text-center">
                <div className="bg-earth-100 rounded-2xl p-8 md:p-12 border border-earth-200">
                    <h3 className="text-2xl font-serif text-earth-900 mb-4">
                        {ctaTitle || `Ready to see how ${moduleName} works for you?`}
                    </h3>
                    <p className="text-earth-700 mb-8 max-w-2xl mx-auto">
                        {ctaDescription || "Join NatureNani today and experience the power of ancient wisdom tailored to your modern life."}
                    </p>
                    <Link
                        to="/app"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold leading-6 text-white bg-sage-600 rounded-full hover:bg-sage-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                    >
                        Open the NatureNani App
                        <ArrowRight size={20} className="ml-2" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode; icon?: React.ReactNode }> = ({ title, children, icon }) => (
    <div className="mb-8 last:mb-0">
        <div className="flex items-center gap-3 mb-3">
            {icon && <div className="text-sage-500">{icon}</div>}
            <h3 className="text-xl font-bold text-sage-900">{title}</h3>
        </div>
        <div className="text-sage-700 leading-relaxed pl-0 md:pl-0">
            {children}
        </div>
    </div>
);

export const ConsultationPage: React.FC = () => {
    return (
        <PlatformPageLayout
            title="Consultation: Understanding Your Body's Language"
            subtitle="Your starting point for personalized natural wellness"
            moduleName="Consultation"
            icon={<Brain className="w-10 h-10 text-white" />}
            heroImage="https://images.unsplash.com/photo-1544367563-12123d8965cd?q=80&w=2070&auto=format&fit=crop"
            ctaTitle="Ready to Begin?"
            ctaDescription="Start your consultation and discover what your body has been trying to tell you."
        >
            <div className="prose prose-sage max-w-none">

                {/* What Is a Consultation? */}
                <div className="mb-12">
                    <h3 className="text-2xl font-bold text-sage-900 mb-4">What Is a Consultation?</h3>
                    <p className="text-lg text-sage-800 leading-relaxed mb-4">
                        The Consultation is NatureNani's interactive health dialogue—not a simple symptom checker, but a guided conversation designed to uncover the root causes of your health concerns.
                    </p>
                    <p className="text-lg text-sage-800 leading-relaxed">
                        Through thoughtful questions about your lifestyle, environment, and physical signals, we help you understand how your unique body constitution interacts with your daily life—and what it needs to return to balance.
                    </p>
                </div>

                <div className="space-y-8 my-8">
                    {/* How It Works */}
                    <div className="bg-sage-50 p-8 rounded-2xl border border-sage-100">
                        <Section title="How It Works" icon={<Activity />}>
                            <ol className="list-decimal pl-5 space-y-4 mt-2">
                                <li className="pl-2"><strong>Guided Discovery:</strong> We ask clarifying questions about your habits, symptoms, diet, sleep, stress, and environment to understand your complete health picture.</li>
                                <li className="pl-2"><strong>Verified Ancient Wisdom:</strong> Every insight is drawn from our curated library of 200+ ancient texts and cross-referenced against 37,000+ verified knowledge points from Ayurveda, Naturopathy, and other nature-based traditions.</li>
                                <li className="pl-2"><strong>Personalized Analysis:</strong> Your responses are analyzed based on your unique body constitution (dosha), age, health conditions, and lifestyle to identify underlying imbalances—not just surface symptoms.</li>
                                <li className="pl-2"><strong>Educational Guidance:</strong> As we explore your health together, you'll learn why certain factors matter—like meal timing, seasonal changes, or stress patterns—and how they specifically affect your body type.</li>
                            </ol>
                        </Section>
                    </div>

                    {/* What We Help You Discover */}
                    <div className="bg-white p-8 rounded-2xl border border-sage-100 shadow-sm">
                        <Section title="What We Help You Discover" icon={<CheckCircle2 />}>
                            <p className="mb-4">Our goal is to move beyond temporary symptom relief and help you understand the "why" behind your discomfort:</p>
                            <ul className="space-y-3 mt-2">
                                <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span><span><strong>Constitutional Imbalances</strong> – Are your body's natural elements out of balance?</span></li>
                                <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span><span><strong>Environmental Factors</strong> – How are seasonal changes or climate affecting you?</span></li>
                                <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span><span><strong>Lifestyle Patterns</strong> – Could your diet, sleep, or daily habits be contributing to the issue?</span></li>
                                <li className="flex gap-2"><span className="text-sage-600 font-bold">✓</span><span><strong>Root Causes</strong> – What's the underlying imbalance that keeps symptoms returning?</span></li>
                            </ul>
                        </Section>
                    </div>

                    {/* What You'll Receive */}
                    <div className="bg-earth-50 p-8 rounded-2xl border border-earth-100">
                        <Section title="What You'll Receive: Your Personal Wellness Blueprint" icon={<Sparkles />}>
                            <p className="mb-4">A comprehensive, easy-to-understand summary that includes:</p>
                            <ul className="space-y-3 mt-2">
                                <li className="flex gap-2"><span className="text-2xl">📋</span><span><strong>Current Health Assessment</strong> – Your body's state explained in plain language</span></li>
                                <li className="flex gap-2"><span className="text-2xl">🌿</span><span><strong>Root Cause Analysis</strong> – What's creating the imbalance</span></li>
                                <li className="flex gap-2"><span className="text-2xl">🍽️</span><span><strong>Personalized Recommendations</strong> – Specific foods, habits, and natural remedies tailored to your constitution</span></li>
                                <li className="flex gap-2"><span className="text-2xl">💬</span><span><strong>Medical Companion Guide</strong> – Information to share with your healthcare provider for more informed conversations</span></li>
                                <li className="flex gap-2"><span className="text-2xl">📈</span><span><strong>Next Steps</strong> – Clear action items to begin restoring balance</span></li>
                            </ul>
                        </Section>
                    </div>

                    {/* Why Consult With NatureNani? */}
                    <div className="bg-sage-600 text-white p-8 rounded-2xl shadow-lg">
                        <div className="flex items-center gap-3 mb-4">
                            <Brain className="text-sage-100" />
                            <h3 className="text-xl font-bold text-white">Why Consult With NatureNani?</h3>
                        </div>
                        <p className="text-sage-100 mb-4">Unlike generic health apps, NatureNani:</p>
                        <ul className="list-disc pl-5 space-y-2 text-sage-50">
                            <li>Personalizes guidance to YOUR unique body constitution</li>
                            <li>Grounds every recommendation in verified ancient texts—not internet trends</li>
                            <li>Teaches you to recognize your body's signals for long-term wellness</li>
                            <li>Works alongside your medical care, not against it</li>
                        </ul>
                    </div>

                    <div className="text-center text-sage-600 text-sm italic mt-8">
                        Most consultations take 5-10 minutes. Your personalized wellness blueprint will be available immediately.
                    </div>
                </div>
            </div>
        </PlatformPageLayout>
    );
};

export const BotanicalRxPage: React.FC = () => {
    return (
        <PlatformPageLayout
            title="Botanical Rx: Herbal Remedies, Personalized"
            subtitle="Ancient herbal wisdom meets modern personalization"
            moduleName="Botanical Rx"
            icon={<Leaf className="w-10 h-10 text-white" />}
            heroImage="https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?q=80&w=2574&auto=format&fit=crop"
            ctaTitle="Ready to Explore Herbal Wisdom?"
            ctaDescription="Unlock Botanical Rx with NatureNani Premium"
        >
            <div className="prose prose-sage max-w-none">

                <div className="flex items-center gap-2 mb-6">
                    <Sparkles className="text-amber-500 fill-amber-500 w-5 h-5" />
                    <span className="text-amber-600 font-bold uppercase tracking-wider text-sm">Premium Feature</span>
                </div>

                <div className="mb-8">
                    <p className="text-sm text-sage-600 italic mb-6 border-l-4 border-sage-300 pl-3">
                        "Rx" stands for "Recommendations," not prescriptions. This is educational guidance only.
                    </p>
                </div>

                <div className="space-y-8 my-8">
                    {/* What Is Botanical Rx? */}
                    <div className="mb-8">
                        <h3 className="text-2xl font-bold text-sage-900 mb-4">What Is Botanical Rx?</h3>
                        <p className="text-lg text-sage-800 leading-relaxed">
                            Botanical Rx is your personalized guide to understanding nature's healing remedies. We translate centuries of herbal wisdom into clear, safe, actionable guidance—tailored specifically to your body constitution and health needs.
                        </p>
                    </div>

                    {/* What You'll Receive */}
                    <div className="bg-white p-8 rounded-2xl border border-sage-100 shadow-sm">
                        <h3 className="text-2xl font-bold text-sage-900 mb-4">What You'll Receive</h3>
                        <p className="text-lg text-sage-800 mb-4">When you ask a health question in consultation, Botanical Rx provides:</p>
                        <ul className="space-y-3 text-sage-700">
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🌿</span> <strong>Specific Herbal Remedies</strong> – Which herbs address your concern</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">💊</span> <strong>Traditional Dosages</strong> – How much to use based on historical practices</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">⏰</span> <strong>Timing & Preparation</strong> – When and how to prepare each remedy</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🎯</span> <strong>Clinical Effects</strong> – Why each herb supports your body</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">📚</span> <strong>Source Citations</strong> – Verified from 200+ ancient texts</li>
                        </ul>
                        <p className="text-sm text-sage-600 mt-4 italic">All recommendations are filtered for your allergies, body constitution, and health conditions.</p>
                    </div>

                    {/* How It Works */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12 text-center">
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">1</div>
                            <h4 className="font-bold text-sage-900 mb-2">Start a Consultation</h4>
                            <p className="text-sage-700 text-sm">Ask any health question</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">2</div>
                            <h4 className="font-bold text-sage-900 mb-2">Get Botanical Rx</h4>
                            <p className="text-sage-700 text-sm">Receive personalized herbal recommendations in an easy-to-read table</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">3</div>
                            <h4 className="font-bold text-sage-900 mb-2">Save to Library</h4>
                            <p className="text-sage-700 text-sm">Keep remedies you want to reference again</p>
                        </div>
                    </div>

                    {/* Example: What a Botanical Rx Looks Like */}
                    <div className="bg-sage-50 p-8 rounded-2xl border border-sage-200">
                        <h3 className="text-xl font-bold text-sage-900 mb-4">Example: What a Botanical Rx Looks Like</h3>
                        <p className="text-sage-700 mb-6">Here's what you receive after a consultation about digestion:</p>

                        <div className="bg-[#f9f9f9] p-6 rounded-lg border border-gray-200 shadow-sm">
                            <h4 className="font-bold text-gray-800 text-lg mb-4 text-center">Clinical Protocol for Digestion</h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-300">
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Botanical Rx</th>
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Dosage</th>
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Preparation</th>
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Frequency</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm text-gray-700">
                                        <tr className="border-b border-gray-200">
                                            <td className="p-3 font-medium">Fresh Lemon Juice</td>
                                            <td className="p-3">1/2 Lemon</td>
                                            <td className="p-3">Mixed in warm water</td>
                                            <td className="p-3">Every morning (empty stomach)</td>
                                        </tr>
                                        <tr>
                                            <td className="p-3 font-medium">Buttermilk (Takra)</td>
                                            <td className="p-3">200ml</td>
                                            <td className="p-3">Churned with cumin/ginger</td>
                                            <td className="p-3">After lunch</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-4 text-xs text-gray-500 border-t border-gray-200 pt-3">
                                <p className="mb-1">Clinical Effects, preparation details, and safety considerations included in full recommendation.</p>
                                <p className="font-medium">Source: Traditional Ayurvedic Samhitas and Naturopathic Clinical Guidelines</p>
                            </div>
                        </div>
                    </div>

                    {/* Your Safety Is Our Priority */}
                    <div className="bg-red-50 p-8 rounded-2xl border border-red-100">
                        <div className="flex items-center gap-3 mb-6">
                            <ShieldCheck className="text-red-600 w-8 h-8" />
                            <h3 className="text-2xl font-bold text-red-900 border-b-2 border-red-200 pb-2 w-full">Your Safety Is Our Priority</h3>
                        </div>

                        <div className="space-y-4">
                            <h4 className="font-bold text-red-800 flex items-center gap-2"><Activity className="w-5 h-5" /> Important Safety Information:</h4>
                            <ul className="list-disc pl-5 space-y-2 text-red-800 text-sm">
                                <li>This is educational guidance based on traditional practices - not medical advice</li>
                                <li>Always consult your healthcare provider before starting new herbal supplements</li>
                                <li>Especially important if you have medical conditions, take medications, or are pregnant/nursing</li>
                                <li>Recommendations are filtered for your allergies, but always verify with your doctor</li>
                                <li>"Natural" does not mean "risk-free" - herbs can have side effects and drug interactions</li>
                            </ul>
                            <div className="mt-6 text-xs text-red-700 italic border-t border-red-200 pt-4">
                                These statements have not been evaluated by the FDA. Not intended to diagnose, treat, cure, or prevent any disease.
                            </div>
                        </div>
                    </div>

                    {/* Save & Organize */}
                    <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm text-center">
                        <h3 className="text-xl font-bold text-sage-900 mb-2 flex items-center justify-center gap-2"><BookMarked className="text-sage-600" /> Save & Organize Your Remedies</h3>
                        <p className="text-sage-700 mb-2 font-medium">Saved Library (Premium Feature)</p>
                        <p className="text-sage-600 text-sm">Save your Botanical Rx recommendations to the Library and access them anytime</p>
                    </div>

                    <div className="text-center mt-8">
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/app" className="py-3 px-6 bg-sage-600 text-white font-bold rounded-xl hover:bg-sage-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                Start 14-Day Free Trial
                            </Link>
                            <Link to="/plan-comparison" className="py-3 px-6 bg-white text-sage-600 border border-sage-200 font-medium rounded-xl hover:bg-sage-50 transition-colors">
                                Compare Plans
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </PlatformPageLayout>
    );
};

export const YogaAidPage: React.FC = () => {
    return (
        <PlatformPageLayout
            title="Yoga Aid: Movement as Medicine"
            subtitle="Personalized yoga practices rooted in ancient healing wisdom"
            moduleName="Yoga Aid"
            icon={<Flower2 className="w-10 h-10 text-white" />}
            heroImage="https://images.unsplash.com/photo-1545205566-3d4555809325?q=80&w=2621&auto=format&fit=crop"
            ctaTitle="Ready to Move Toward Balance?"
            ctaDescription="Unlock Yoga Aid with NatureNani Premium"
        >
            <div className="prose prose-sage max-w-none">

                <div className="flex items-center gap-2 mb-6">
                    <Sparkles className="text-amber-500 fill-amber-500 w-5 h-5" />
                    <span className="text-amber-600 font-bold uppercase tracking-wider text-sm">Premium Feature</span>
                </div>

                {/* What Is Yoga Aid? */}
                <div className="mb-12">
                    <h3 className="text-2xl font-bold text-sage-900 mb-4">What Is Yoga Aid?</h3>
                    <p className="text-lg text-sage-800 leading-relaxed">
                        Yoga Aid is your personalized movement prescription—not generic fitness routines, but specific yoga poses, restorative practices, and breathing exercises designed to address YOUR body's imbalances and support natural healing.
                    </p>
                </div>

                <div className="space-y-8 my-8">

                    {/* What You'll Receive */}
                    <div className="bg-white p-8 rounded-2xl border border-sage-100 shadow-sm">
                        <h3 className="text-2xl font-bold text-sage-900 mb-4">What You'll Receive</h3>
                        <p className="text-lg text-sage-800 mb-4">When you ask a health question in consultation, Yoga Aid provides:</p>
                        <ul className="space-y-3 text-sage-700">
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🧘</span> <strong>Targeted Yoga Poses (Asanas)</strong> – Specific postures for your imbalance</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🌬️</span> <strong>Breathing Exercises (Pranayama)</strong> – Techniques to balance your nervous system</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🌿</span> <strong>Restorative Routines</strong> – Gentle practices for deep healing</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🚶</span> <strong>Complementary Movement</strong> – Nature walks or grounding activities</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">📋</span> <strong>Complete Guidance</strong> – Benefits, practice instructions, contraindications, and visual illustrations</li>
                        </ul>
                        <p className="text-sm text-sage-600 mt-4 italic">All practices are personalized to your body constitution, health conditions, and physical capacity.</p>
                    </div>

                    {/* How It Works */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12 text-center">
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">1</div>
                            <h4 className="font-bold text-sage-900 mb-2">Start a Consultation</h4>
                            <p className="text-sage-700 text-sm">Share your health concern or symptoms</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">2</div>
                            <h4 className="font-bold text-sage-900 mb-2">Get Yoga Aid Protocol</h4>
                            <p className="text-sage-700 text-sm">Receive a structured therapeutic practice plan</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">3</div>
                            <h4 className="font-bold text-sage-900 mb-2">Save to Library</h4>
                            <p className="text-sage-700 text-sm">Keep practices you want to use regularly</p>
                        </div>
                    </div>

                    {/* Example: What a Yoga Aid Protocol Looks Like */}
                    <div className="bg-sage-50 p-8 rounded-2xl border border-sage-200">
                        <h3 className="text-xl font-bold text-sage-900 mb-4">Example: What a Yoga Aid Protocol Looks Like</h3>
                        <p className="text-sage-700 mb-6">Here's what you receive after a consultation about migraines:</p>

                        <div className="bg-[#f9f9f9] p-6 rounded-lg border border-gray-200 shadow-sm">
                            <h4 className="font-bold text-gray-800 text-lg mb-2 text-center">Therapeutic Protocol for Migraine</h4>
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <span className="bg-amber-100 text-amber-800 text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider">Adaptive Guidance - Acute</span>
                            </div>
                            <p className="text-center text-sm text-gray-600 mb-6 italic">Advise following the plan for at least 7 days or until symptoms resolve.</p>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-gray-300">
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Phase</th>
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Action</th>
                                            <th className="p-3 text-sm font-bold text-gray-700 bg-gray-100">Purpose</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm text-gray-700">
                                        <tr className="border-b border-gray-200">
                                            <td className="p-3 font-medium text-sage-700">Foundation</td>
                                            <td className="p-3 font-medium">Child's Pose (Balasana)</td>
                                            <td className="p-3">Calms the central nervous system and relieves tension in the scalp and neck muscles</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="p-3 font-medium text-sage-700">Activation</td>
                                            <td className="p-3 font-medium">Downward-Facing Dog (Adho Mukha Svanasana)</td>
                                            <td className="p-3">Improves cerebral blood flow and stretches the muscles of the spine and legs</td>
                                        </tr>
                                        <tr className="border-b border-gray-200">
                                            <td className="p-3 font-medium text-sage-700">Internal Balance</td>
                                            <td className="p-3 font-medium">Alternate Nostril Breathing (Nadi Shodhana)</td>
                                            <td className="p-3">Reduces cerebral hyperexcitability and stabilizes the trigeminal/vascular system</td>
                                        </tr>
                                        <tr>
                                            <td className="p-3 font-medium text-sage-700">Nature Integration</td>
                                            <td className="p-3 font-medium">Gentle Nature Walk</td>
                                            <td className="p-3">Provides low-impact aerobic movement that supports vascular health without triggering inflammation</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="mt-6 bg-white p-4 rounded-lg border border-gray-100">
                                <p className="font-bold text-gray-700 text-sm mb-2">Each pose includes:</p>
                                <ul className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Visual illustration</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Step-by-step practice instructions</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Therapeutic benefits</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Safety contraindications</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Modifications for your needs</li>
                                </ul>
                                <div className="mt-4 text-center">
                                    <span className="text-sage-600 text-xs font-medium border-b border-dashed border-sage-300">Tap any pose to view complete details</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Safety Warning */}
                    <div className="bg-red-50 p-8 rounded-2xl border border-red-100">
                        <div className="flex items-center gap-3 mb-4">
                            <ShieldCheck className="text-red-500 w-6 h-6" />
                            <h3 className="text-xl font-bold text-red-900">Your Safety Is Our Priority</h3>
                        </div>

                        <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2"><Activity className="w-4 h-4" /> Important Safety Information:</h4>

                        <ul className="list-disc pl-5 space-y-2 text-red-800 text-sm mb-6">
                            <li>This is educational guidance based on traditional practices - not medical advice</li>
                            <li>Always consult your healthcare provider before starting new physical practices</li>
                            <li>Especially important if you have injuries, chronic conditions, or physical limitations</li>
                            <li>Practices are personalized, but listen to your body and stop if you feel pain</li>
                            <li>Modify or skip poses that don't feel right for you</li>
                        </ul>

                        <div className="text-center">
                            <Link to="/disclaimer" className="text-red-700 text-sm font-medium hover:underline">
                                For complete safety guidelines, see our Safety Information page.
                            </Link>
                            <div className="mt-2 text-xs text-red-700 italic">
                                Not intended to diagnose, treat, cure, or prevent any disease.
                            </div>
                        </div>
                    </div>

                    {/* Save & Build */}
                    <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm text-center">
                        <h3 className="text-xl font-bold text-sage-900 mb-2 flex items-center justify-center gap-2"><BookMarked className="text-sage-600" /> Save & Build Your Practice</h3>
                        <p className="text-sage-700 mb-4 font-medium">Saved Library (Premium Feature)</p>
                        <p className="text-sage-600 text-sm mb-4">Save your Yoga Aid protocols to the Library and create personal practice routines for different needs.</p>

                        <div className="flex flex-wrap justify-center gap-2 text-xs text-sage-600">
                            <span className="bg-sage-50 px-3 py-1 rounded-full border border-sage-100">Morning energizing sequences</span>
                            <span className="bg-sage-50 px-3 py-1 rounded-full border border-sage-100">Evening wind-down practices</span>
                            <span className="bg-sage-50 px-3 py-1 rounded-full border border-sage-100">Quick stress-relief protocols</span>
                            <span className="bg-sage-50 px-3 py-1 rounded-full border border-sage-100">Condition-specific therapeutic routines</span>
                        </div>
                    </div>

                    <div className="text-center mt-8">
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/app" className="py-3 px-6 bg-sage-600 text-white font-bold rounded-xl hover:bg-sage-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                Start 14-Day Free Trial
                            </Link>
                            <Link to="/plan-comparison" className="py-3 px-6 bg-white text-sage-600 border border-sage-200 font-medium rounded-xl hover:bg-sage-50 transition-colors">
                                Compare Plans
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </PlatformPageLayout>
    );
};

export const NutriHealPage: React.FC = () => {
    return (
        <PlatformPageLayout
            title="Nutri Heal: Food as Medicine"
            subtitle="Personalized nutrition guidance rooted in ancient healing wisdom"
            moduleName="Nutri Heal"
            icon={<Utensils className="w-10 h-10 text-white" />}
            heroImage="https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=80&w=2670&auto=format&fit=crop"
            ctaTitle="Ready to Transform Your Kitchen into Your Pharmacy?"
            ctaDescription="Unlock Nutri Heal with NatureNani Premium"
        >
            <div className="prose prose-sage max-w-none">
                <div className="flex items-center gap-2 mb-6">
                    <Sparkles className="text-amber-500 fill-amber-500 w-5 h-5" />
                    <span className="text-amber-600 font-bold uppercase tracking-wider text-sm">Premium Feature</span>
                </div>

                {/* What Is Nutri Heal? */}
                <div className="mb-12">
                    <h3 className="text-2xl font-bold text-sage-900 mb-4">What Is Nutri Heal?</h3>
                    <p className="text-lg text-sage-800 leading-relaxed mb-4">
                        Nutri Heal transforms your kitchen into your first pharmacy. This isn't about restrictive dieting or calorie counting—it's about understanding how specific foods, spices, and preparation methods interact with YOUR unique body to prevent imbalances and support natural healing.
                    </p>
                </div>

                <div className="space-y-8 my-8">
                    {/* What You'll Receive */}
                    <div className="bg-white p-8 rounded-2xl border border-sage-100 shadow-sm">
                        <h3 className="text-2xl font-bold text-sage-900 mb-4">What You'll Receive</h3>
                        <p className="text-lg text-sage-800 mb-4">When you ask a health question in consultation, Nutri Heal provides:</p>
                        <ul className="space-y-3 text-sage-700">
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🍽️</span> <strong>Multi-Day Meal Plans</strong> – Breakfast, lunch, and dinner for your specific condition</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🥗</span> <strong>Complete Recipes</strong> – Ingredients, preparation steps, and medicinal purpose</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🌶️</span> <strong>Healing Foods</strong> – Which ingredients address your imbalance</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">⏰</span> <strong>Meal Timing</strong> – When to eat for optimal therapeutic benefit</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">📸</span> <strong>Visual Inspiration</strong> – Beautiful food photography for each meal</li>
                            <li className="flex gap-2"><span className="text-green-600 font-bold">🎯</span> <strong>Clinical Rationale</strong> – Why each recipe supports your body's healing</li>
                        </ul>
                        <p className="text-sm text-sage-600 mt-4 italic">All meal plans are filtered for your dietary preferences, allergies, and body constitution.</p>
                    </div>

                    {/* How It Works */}
                    <div className="grid md:grid-cols-3 gap-6 mb-12 text-center">
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">1</div>
                            <h4 className="font-bold text-sage-900 mb-2">Start a Consultation</h4>
                            <p className="text-sage-700 text-sm">Share your health concern or wellness goal</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">2</div>
                            <h4 className="font-bold text-sage-900 mb-2">Get Nutri Heal Plan</h4>
                            <p className="text-sage-700 text-sm">Receive a personalized multi-day meal plan with recipes</p>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-xl border border-sage-100">
                            <div className="bg-sage-100 w-12 h-12 rounded-full flex items-center justify-center text-sage-700 font-bold text-xl mx-auto mb-4">3</div>
                            <h4 className="font-bold text-sage-900 mb-2">Save to Library</h4>
                            <p className="text-sage-700 text-sm">Keep meal plans you want to use again</p>
                        </div>
                    </div>

                    {/* Example: What a Nutri Heal Plan Looks Like */}
                    <div className="bg-sage-50 p-8 rounded-2xl border border-sage-200">
                        <h3 className="text-xl font-bold text-sage-900 mb-4">Example: What a Nutri Heal Plan Looks Like</h3>
                        <p className="text-sage-700 mb-6">Here's what you receive after a consultation about muscle and joint health:</p>

                        <div className="bg-[#f9f9f9] p-6 rounded-lg border border-gray-200 shadow-sm">
                            <h4 className="font-bold text-gray-800 text-lg mb-4 text-center">Meal Plan for Muscle Strength and Joint Health</h4>

                            <div className="space-y-6">
                                {/* Day 1 */}
                                <div>
                                    <h5 className="font-bold text-sage-900 mb-3 border-b border-sage-200 pb-1">DAY 1</h5>
                                    <div className="grid gap-4">
                                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xl">🌅</span>
                                                <span className="font-bold text-sage-800 text-sm">BREAKFAST</span>
                                            </div>
                                            <p className="font-medium text-gray-900 mb-1">Greek Yogurt with Berries and Walnuts</p>
                                            <p className="text-xs text-sage-600 italic">"High protein for muscle repair and Omega-3 fatty acids for joint inflammation reduction."</p>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xl">🌞</span>
                                                <span className="font-bold text-sage-800 text-sm">LUNCH</span>
                                            </div>
                                            <p className="font-medium text-gray-900 mb-1">Grilled Salmon Salad with Turmeric Dressing</p>
                                            <p className="text-xs text-sage-600 italic">"Salmon provides vitamin D and Omega-3s for joint health while turmeric acts as a natural anti-inflammatory."</p>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-100">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xl">🌙</span>
                                                <span className="font-bold text-sage-800 text-sm">DINNER</span>
                                            </div>
                                            <p className="font-medium text-gray-900 mb-1">Lean Beef and Quinoa Stir-fry</p>
                                            <p className="text-xs text-sage-600 italic">"Iron and protein from beef support muscle strength while vitamin C from broccoli aids collagen synthesis."</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Day 2 */}
                                <div>
                                    <h5 className="font-bold text-sage-900 mb-3 border-b border-sage-200 pb-1">DAY 2</h5>
                                    <div className="bg-white p-4 rounded-lg border border-gray-100">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xl">🌅</span>
                                            <span className="font-bold text-sage-800 text-sm">BREAKFAST</span>
                                        </div>
                                        <p className="font-medium text-gray-900 mb-1">Turmeric Scrambled Eggs with Spinach</p>
                                        <p className="text-xs text-sage-600 italic">"Eggs provide high-quality protein for muscles; turmeric and spinach support joint mobility."</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 bg-white p-4 rounded-lg border border-gray-100 text-center">
                                <p className="font-bold text-gray-700 text-sm mb-3">Tap any recipe card to view:</p>
                                <ul className="text-left grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-gray-600 max-w-lg mx-auto">
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Complete ingredient list with amounts</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Step-by-step preparation instructions</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Detailed medicinal purpose</li>
                                    <li className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Nutritional benefits explained</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* What Each Recipe Includes */}
                    <div className="bg-white p-8 rounded-2xl border border-sage-100 shadow-sm">
                        <Section title="What Each Recipe Includes" icon={<BookOpen />}>
                            <p className="mb-4">When you tap on a recipe, you'll see:</p>
                            <ul className="space-y-4">
                                <li className="pl-2">
                                    <strong>📋 Ingredients:</strong> Precise measurements for each ingredient.
                                </li>
                                <li className="pl-2">
                                    <strong>👨🍳 Preparation Steps:</strong> Clear, numbered cooking instructions.
                                </li>
                                <li className="pl-2">
                                    <strong>🌿 Medicinal Purpose:</strong> Detailed explanation of how this meal targets your specific ailment and which ingredients provide therapeutic benefits.
                                </li>
                                <li className="pl-2">
                                    <strong>🎯 Clinical Benefits:</strong> Why this combination of foods supports your body's healing process.
                                </li>
                            </ul>
                        </Section>
                    </div>

                    {/* Safety Warning */}
                    <div className="bg-red-50 p-8 rounded-2xl border border-red-100">
                        <div className="flex items-center gap-3 mb-4">
                            <ShieldCheck className="text-red-500 w-6 h-6" />
                            <h3 className="text-xl font-bold text-red-900">Your Safety Is Our Priority</h3>
                        </div>

                        <h4 className="font-bold text-red-800 mb-2 flex items-center gap-2"><Activity className="w-4 h-4" /> Important Dietary Information:</h4>

                        <ul className="list-disc pl-5 space-y-2 text-red-800 text-sm mb-6">
                            <li>This is educational nutritional guidance based on traditional practices - not medical nutrition therapy</li>
                            <li>Always consult your healthcare provider before making major dietary changes</li>
                            <li>Especially important if you have food allergies, diabetes, kidney disease, or other conditions affected by diet</li>
                            <li>Meal plans are filtered for your stated allergies and preferences, but always verify ingredients</li>
                            <li>Individual nutritional needs vary - what works for others may not work for you</li>
                        </ul>

                        <div className="text-center">
                            <Link to="/disclaimer" className="text-red-700 text-sm font-medium hover:underline">
                                For complete safety guidelines, see our Safety Information page.
                            </Link>
                            <div className="mt-2 text-xs text-red-700 italic">
                                Not intended to diagnose, treat, cure, or prevent any disease.
                            </div>
                        </div>
                    </div>

                    {/* Save & Organize */}
                    <div className="grid md:grid-cols-2 gap-8">
                        <div className="bg-white p-6 rounded-2xl border border-sage-100 shadow-sm">
                            <h3 className="text-xl font-bold text-sage-900 mb-4 flex items-center gap-2"><BookMarked className="text-sage-600" /> Save & Organize Your Meal Plans</h3>
                            <p className="text-sage-700 mb-2 font-medium">Saved Library (Premium Feature)</p>
                            <p className="text-sage-600 text-sm mb-4">Save your Nutri Heal plans to the Library and build your personal recipe collection:</p>
                            <ul className="space-y-2 text-sm text-sage-700 ml-4 list-disc">
                                <li>Condition-specific meal plans</li>
                                <li>Seasonal eating guides</li>
                                <li>Quick healing recipes</li>
                                <li>Anti-inflammatory favorites</li>
                            </ul>
                        </div>
                        <div className="bg-sage-50 p-6 rounded-2xl border border-sage-100">
                            <h3 className="text-xl font-bold text-sage-900 mb-4 flex items-center gap-2"><CheckCircle2 className="text-sage-600" /> Allergen-Safe & Preference-Friendly</h3>
                            <p className="text-sage-600 text-sm mb-4">Scale your recommendations with peace of mind. Automatically filtered for:</p>
                            <ul className="space-y-2 text-sm text-sage-700 ml-4 list-disc">
                                <li>Your identified food allergies</li>
                                <li>Your dietary preferences (vegetarian, vegan, etc.)</li>
                                <li>Your health conditions and restrictions</li>
                            </ul>
                            <p className="text-sage-700 text-sm mt-4 font-medium">All recommendations honor your values and keep you safe.</p>
                        </div>
                    </div>

                    <div className="text-center mt-8">
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/app" className="py-3 px-6 bg-sage-600 text-white font-bold rounded-xl hover:bg-sage-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                                Start 14-Day Free Trial
                            </Link>
                            <Link to="/plan-comparison" className="py-3 px-6 bg-white text-sage-600 border border-sage-200 font-medium rounded-xl hover:bg-sage-50 transition-colors">
                                Compare Plans
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </PlatformPageLayout>
    );
};
